"use client";

import { useEffect, useState } from "react";
import clsx from "clsx";
import {
  ChevronDown,
  RefreshCw,
  Search,
  SlidersHorizontal,
  Users,
} from "lucide-react";
import EmptyState from "./EmptyState";
import Pagination from "./Pagination";
import SearchInput from "./SearchInput";
import { clampPage } from "../utils/pagination";
import { useLocale, useTranslations } from "next-intl";
import { usePathname } from "next/navigation";
import { getClientRegion } from "@/services/region";
import FormSelect from "@/components/FormSelect";
import {
  TeachersGridSkeleton,
  TeacherFiltersSkeleton,
} from "@/components/skeletons/TeacherSkeletons";
import UserGrid from "./UserGrid";
import {
  getTeacherFilterOptionsApi,
  getTeachersApi,
  type TeacherFilterOption,
  type TeacherListUser,
} from "../services/teachers.api";
import { partitionByOther } from "../utils/categoryGrouping";

const TEACHERS_PER_PAGE = 12;
const FILTERS_PANEL_ID = "teacher-filters-panel";
const SEARCH_DEBOUNCE_MS = 700;
/**
 * Sentinel option values for the category filter's in-place "Other" drill-in
 * (mirrors the Free Trial selector). Category values are numeric-string ids, so
 * these never collide with a real category; they are transient actions handled
 * in onChange, never stored as the selected category.
 */
const OTHER_CATEGORY_VALUE = "__other__";
const BACK_CATEGORY_VALUE = "__back__";
/**
 * Below this the query is ignored entirely rather than sent. One or two
 * characters match most of the roster, so the request costs a round trip to
 * return something no narrower than the unfiltered list.
 */
const MIN_SEARCH_LENGTH = 3;

export default function TeachersPageContent() {
  const locale = useLocale();
  const pathname = usePathname();
  const t = useTranslations("web.teachers");
  const [categoryOptions, setCategoryOptions] = useState<TeacherFilterOption[]>(
    [],
  );
  const [languageOptions, setLanguageOptions] = useState<TeacherFilterOption[]>(
    [],
  );
  const [metadataLoading, setMetadataLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("");
  // Drill-in state for the category filter's "Other" group (see the category
  // FormSelect below). `selectedCategory` stays the real id in both views.
  const [otherCategoryOpen, setOtherCategoryOpen] = useState(false);
  const [categoryMenuOpen, setCategoryMenuOpen] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState("");
  const [selectedGender, setSelectedGender] = useState("");
  const [appliedCategory, setAppliedCategory] = useState("");
  const [appliedLanguage, setAppliedLanguage] = useState("");
  const [appliedGender, setAppliedGender] = useState("");
  const [teachers, setTeachers] = useState<TeacherListUser[]>([]);
  const [teachersLoading, setTeachersLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  // Collapsed by default, but only matters below `lg` - the desktop sidebar
  // ignores this and stays open.
  const [filtersOpen, setFiltersOpen] = useState(false);

  const genderOptions: TeacherFilterOption[] = [
    { value: "male", label: t("male") },
    { value: "female", label: t("female") },
  ];

  // Primary categories render directly; the `show_in_other` ones sit behind an
  // in-place "Other" drill-in. Backend already orders by position, so the split
  // preserves it. `inOtherView` decides which option set the single select shows.
  const { primary: primaryCategories, other: otherCategories } =
    partitionByOther(categoryOptions);
  const isSelectedCategoryOther = otherCategories.some(
    (option) => option.value === selectedCategory,
  );
  const inOtherView =
    otherCategories.length > 0 && (otherCategoryOpen || isSelectedCategoryOther);

  useEffect(() => {
    let isActive = true;

    const loadFilterOptions = async () => {
      setMetadataLoading(true);

      try {
        const metadata = await getTeacherFilterOptionsApi(locale);

        if (!isActive) {
          return;
        }

        setCategoryOptions(metadata.categories);
        setLanguageOptions(metadata.languages);
      } catch {
        if (!isActive) {
          return;
        }

        setCategoryOptions([]);
        setLanguageOptions([]);
      } finally {
        if (isActive) {
          setMetadataLoading(false);
        }
      }
    };

    void loadFilterOptions();

    return () => {
      isActive = false;
    };
  }, [locale]);

  // What the typed value is actually worth as a query: anything shorter than
  // the minimum collapses to "" and behaves exactly like an empty box, so
  // backspacing from 3 characters to 2 restores the full list.
  const effectiveSearch =
    searchQuery.trim().length >= MIN_SEARCH_LENGTH ? searchQuery.trim() : "";

  // The search box drives a request now that the backend matches names itself
  // (including the Arabic name, which the client could not see), so the typed
  // value is debounced before it becomes a query.
  useEffect(() => {
    if (effectiveSearch === appliedSearch) {
      return;
    }

    const timer = setTimeout(() => {
      setAppliedSearch(effectiveSearch);
      setCurrentPage(1);
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [effectiveSearch, appliedSearch]);

  // True from the first keystroke until the resulting page has landed, so the
  // field shows a spinner across both the debounce wait and the request.
  const isSearching =
    searchQuery.trim().length > 0 &&
    (effectiveSearch !== appliedSearch || (teachersLoading && !!appliedSearch));

  useEffect(() => {
    let isActive = true;

    const loadTeachers = async () => {
      setTeachersLoading(true);

      try {
        const regionSlug = getClientRegion(pathname) || undefined;
        const { teachers: pageTeachers, meta } = await getTeachersApi(locale, {
          categoryId: appliedCategory,
          languageCode: appliedLanguage,
          gender: appliedGender,
          regionSlug,
          search: appliedSearch,
          page: currentPage,
          perPage: TEACHERS_PER_PAGE,
        });

        if (!isActive) {
          return;
        }

        setTeachers(pageTeachers);
        setLastPage(meta?.lastPage ?? 1);
      } catch {
        if (!isActive) {
          return;
        }

        setTeachers([]);
        setLastPage(1);
      } finally {
        if (isActive) {
          setTeachersLoading(false);
        }
      }
    };

    void loadTeachers();

    return () => {
      isActive = false;
    };
  }, [
    appliedCategory,
    appliedGender,
    appliedLanguage,
    appliedSearch,
    currentPage,
    locale,
    pathname,
  ]);

  const applyFilters = () => {
    setAppliedCategory(selectedCategory);
    setAppliedLanguage(selectedLanguage);
    setAppliedGender(selectedGender);
    setCurrentPage(1);
    // On mobile the panel sits on top of the results, so leaving it open after
    // Apply hides the very thing the user just asked for. No effect on desktop,
    // where the sidebar is forced visible.
    setFiltersOpen(false);
  };

  const resetFilters = () => {
    setSelectedCategory("");
    setOtherCategoryOpen(false);
    setSelectedLanguage("");
    setSelectedGender("");
    setAppliedCategory("");
    setAppliedLanguage("");
    setAppliedGender("");
    setSearchQuery("");
    setAppliedSearch("");
    setCurrentPage(1);
  };

  const activeFilterCount = [appliedCategory, appliedLanguage, appliedGender].filter(
    Boolean,
  ).length;

  // `teachers` is already exactly one page, filtered and searched by the
  // backend - the page count comes from `meta.last_page` rather than being
  // derived from a list length the client no longer holds in full.
  const safePage = clampPage(currentPage, lastPage);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(clampPage(page, lastPage));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    // Filters as a sidebar beside the results, rather than a horizontal strip
    // stacked above them. The row layout forced each control into a narrow
    // 2-4 column slice and pushed the grid below the fold; a sidebar gives the
    // controls room to breathe and puts results and filters side by side.
    // Below `lg` it collapses back to a stacked layout. Column order is
    // automatically mirrored in RTL, so the sidebar sits on the start side in
    // both locales.
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[280px_1fr] lg:gap-8">
      <aside className="lg:sticky lg:top-24 lg:self-start">
        <div className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm">
          {/* Below `lg` the sidebar stacks on top of the results, so leaving the
              three selects and both action buttons expanded pushed the teacher
              grid off the first screen entirely. On mobile the header is a
              toggle; from `lg` up it reverts to a plain heading and the panel is
              always open, so the desktop sidebar is unchanged. */}
          <button
            type="button"
            onClick={() => setFiltersOpen((open) => !open)}
            aria-expanded={filtersOpen}
            aria-controls={FILTERS_PANEL_ID}
            className="flex w-full items-center justify-between gap-2 border-b border-slate-100 pb-4 text-start lg:pointer-events-none"
          >
            <span className="flex items-center gap-2 text-base font-bold text-primary">
              <SlidersHorizontal size={18} className="text-secondary" />
              {t("filters")}
            </span>

            <span className="flex items-center gap-2">
              {activeFilterCount > 0 ? (
                <span className="rounded-[10px] bg-secondary/10 px-2.5 py-0.5 text-xs font-bold text-secondary">
                  {activeFilterCount}
                </span>
              ) : null}
              <ChevronDown
                size={18}
                aria-hidden="true"
                className={clsx(
                  "text-secondary transition-transform duration-200 lg:hidden",
                  filtersOpen && "rotate-180",
                )}
              />
            </span>
          </button>

          <div
            id={FILTERS_PANEL_ID}
            className={clsx("mt-5 space-y-4", !filtersOpen && "hidden lg:block")}
          >
            <FormSelect
              label={t("category")}
              options={
                inOtherView
                  ? [
                      { value: BACK_CATEGORY_VALUE, label: t("category_back") },
                      ...otherCategories,
                    ]
                  : [
                      ...primaryCategories,
                      // "›" is bidi-mirrored, so it points the right way under
                      // dir="rtl" without a locale branch.
                      ...(otherCategories.length > 0
                        ? [
                            {
                              value: OTHER_CATEGORY_VALUE,
                              label: `${t("category_other")} ›`,
                            },
                          ]
                        : []),
                    ]
              }
              value={selectedCategory}
              // Controlled menu + closeMenuOnSelect=false so drilling into Other
              // (or going Back) keeps the dropdown open and swaps the option list
              // in place; only picking a real category closes it.
              closeMenuOnSelect={false}
              menuIsOpen={categoryMenuOpen}
              onMenuOpen={() => setCategoryMenuOpen(true)}
              onMenuClose={() => setCategoryMenuOpen(false)}
              onChange={(value) => {
                if (value === OTHER_CATEGORY_VALUE) {
                  setOtherCategoryOpen(true);
                  setCategoryMenuOpen(true);
                  return;
                }
                if (value === BACK_CATEGORY_VALUE) {
                  setOtherCategoryOpen(false);
                  setSelectedCategory("");
                  setCategoryMenuOpen(true);
                  return;
                }
                setOtherCategoryOpen(false);
                setSelectedCategory(value);
                setCategoryMenuOpen(false);
              }}
              placeholder={
                metadataLoading
                  ? t("loading_filters")
                  : inOtherView
                    ? t("select_other_category")
                    : t("select_category")
              }
              isDisabled={metadataLoading || categoryOptions.length === 0}
              isLoading={metadataLoading}
              isSearchable
            />

            <FormSelect
              label={t("language")}
              options={languageOptions}
              value={selectedLanguage}
              onChange={setSelectedLanguage}
              placeholder={
                metadataLoading ? t("loading_filters") : t("select_language")
              }
              isDisabled={metadataLoading || languageOptions.length === 0}
              isLoading={metadataLoading}
            />

            <FormSelect
              label={t("gender")}
              options={genderOptions}
              value={selectedGender}
              onChange={setSelectedGender}
              placeholder={t("select_gender")}
            />
          </div>

          <div
            className={clsx(
              "mt-6 space-y-2 border-t border-slate-100 pt-5",
              !filtersOpen && "hidden lg:block",
            )}
          >
            <button
              type="button"
              onClick={applyFilters}
              className="flex w-full cursor-pointer items-center justify-center gap-2 rounded-[10px] bg-secondary py-3 text-sm font-bold text-white transition-colors hover:bg-primary"
            >
              <Search size={16} />
              {t("apply")}
            </button>

            <button
              type="button"
              onClick={resetFilters}
              className="flex w-full cursor-pointer items-center justify-center gap-2 rounded-[10px] border border-slate-200 py-3 text-sm font-semibold text-slate-500 transition-colors hover:border-slate-300 hover:bg-slate-50"
            >
              <RefreshCw size={15} />
              {t("reset")}
            </button>
          </div>
        </div>
      </aside>

      <div className="min-w-0">
        {metadataLoading &&
        categoryOptions.length === 0 &&
        languageOptions.length === 0 ? (
          <TeacherFiltersSkeleton />
        ) : null}

        <SearchInput
          value={searchQuery}
          onChange={handleSearchChange}
          placeholder={t("search_by_name")}
          isLoading={isSearching}
          hint={
            // Only while the query is too short to run - saying nothing would
            // otherwise read as "searched, found nothing".
            searchQuery.trim().length > 0 && !effectiveSearch
              ? t("search_min_chars", { count: MIN_SEARCH_LENGTH })
              : undefined
          }
          className="mb-6"
        />

        {teachersLoading ? (
          <TeachersGridSkeleton />
        ) : teachers.length > 0 ? (
          <>
            <UserGrid users={teachers} />

            <Pagination
              currentPage={safePage}
              totalPages={lastPage}
              onPageChange={handlePageChange}
              className="mt-10"
            />
          </>
        ) : (
          // A search that matches nothing is a different dead end from filters
          // that match nothing, and clearing the wrong one leaves the user
          // still looking at an empty grid.
          <EmptyState
            icon={<Users size={28} />}
            title={appliedSearch ? t("no_teachers_match") : t("no_teachers")}
            description={
              appliedSearch
                ? t("no_teachers_match_hint")
                : t("no_teachers_hint")
            }
            action={
              <button
                type="button"
                onClick={
                  appliedSearch ? () => handleSearchChange("") : resetFilters
                }
                className="cursor-pointer rounded-[10px] bg-primary px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-secondary"
              >
                {appliedSearch ? t("clear_search") : t("reset")}
              </button>
            }
          />
        )}
      </div>
    </div>
  );
}
